import {
  CLIENT_LOG_MAX_BATCH_SIZE,
  CLIENT_LOG_MAX_DATA_JSON_LENGTH,
  CLIENT_LOG_MAX_MESSAGE_LENGTH,
  ClientLogId,
  ClientLogLevel,
  generateClientLogId,
  generateId,
} from "@fatbees/schemas";

/**
 * Frontend telemetry (docs/client-telemetry.md): arbitrary log entries the
 * app posts back to `POST /api/client-logs` so the debug API can replay
 * what a user's browser actually did. Entries queue in memory and flush in
 * batches; nothing here can throw into, block, or otherwise affect the
 * app — a telemetry failure is silently retried or dropped.
 */

export interface TelemetryEntry {
  /**
   * Minted here, when the entry is queued, and used as the stored row's
   * primary key so the server can drop a re-post. A batch is re-queued
   * whenever its response is lost rather than refused, and by then the
   * server may already have committed it (docs/client-telemetry.md,
   * "Duplicate delivery"), so the retry has to be harmless.
   */
  id: ClientLogId;
  time: Date;
  level: ClientLogLevel;
  message: string;
  data?: unknown;
  path?: string;
}

export interface TelemetryOptions {
  /** Injectable for tests; defaults to the global fetch. */
  fetch?: typeof fetch;
  now?: () => Date;
  endpoint?: string;
}

export const TELEMETRY_ENDPOINT = "/api/client-logs";
export const TELEMETRY_FLUSH_INTERVAL_MS = 5_000;
/** Entries kept while flushes fail (offline); oldest are dropped beyond this. */
export const TELEMETRY_MAX_QUEUE = 500;
/** fetch({ keepalive }) bodies are capped at 64 KiB by browsers. */
const KEEPALIVE_MAX_BODY_BYTES = 60_000;
const MAX_BODY_BYTES = 900_000;
const DATA_PREVIEW_LENGTH = 2_000;
const SESSION_STORAGE_KEY = "fatbees:telemetry-session";

export class Telemetry {
  private queue: TelemetryEntry[] = [];
  private started = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  private removeListeners: (() => void) | null = null;
  private sessionIdValue: string | null = null;

  constructor(private readonly options: TelemetryOptions = {}) {}

  /**
   * Identifies the browser tab: sessionStorage-scoped, so it survives
   * reloads (the persisted caches do too, which is what we debug) but not
   * a new tab. Falls back to an in-memory id where storage is unavailable.
   */
  get sessionId(): string {
    if (this.sessionIdValue) {
      return this.sessionIdValue;
    }

    let stored: string | null = null;
    try {
      stored =
        typeof sessionStorage === "undefined"
          ? null
          : sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      stored = null;
    }

    this.sessionIdValue = stored ?? generateId();

    if (!stored) {
      try {
        sessionStorage?.setItem(SESSION_STORAGE_KEY, this.sessionIdValue);
      } catch {
        // Private mode / quota: an in-memory id is fine.
      }
    }

    return this.sessionIdValue;
  }

  get pending(): number {
    return this.queue.length;
  }

  debug(message: string, data?: unknown): void {
    this.log("debug", message, data);
  }

  info(message: string, data?: unknown): void {
    this.log("info", message, data);
  }

  warn(message: string, data?: unknown): void {
    this.log("warn", message, data);
  }

  error(message: string, data?: unknown): void {
    this.log("error", message, data);
  }

  log(level: ClientLogLevel, message: string, data?: unknown): void {
    this.queue.push({
      id: generateClientLogId(),
      time: this.now(),
      level,
      message: message.slice(0, CLIENT_LOG_MAX_MESSAGE_LENGTH),
      data: snapshot(data),
      path: currentPath(),
    });
    this.trimQueue();
    this.schedule();
  }

  /**
   * Begins flushing (entries logged before this just queue) and records
   * page lifecycle events. `context` is logged with the session-start
   * entry — build id, release, who the session runs as.
   * Returns the stop function, for use as a React effect cleanup.
   */
  start(context: Record<string, unknown> = {}): () => void {
    if (!this.started) {
      this.started = true;
      this.removeListeners = this.listen();
      this.info("session started", {
        ...context,
        url: typeof location === "undefined" ? undefined : location.href,
        userAgent:
          typeof navigator === "undefined" ? undefined : navigator.userAgent,
        online: typeof navigator === "undefined" ? undefined : navigator.onLine,
        // "reload" vs "navigate" vs "back_forward": whether this session
        // is a refresh of a page already showing the problem.
        navigation: navigationType(),
      });
      void this.logStorageEstimate();
    }

    return () => this.stop();
  }

  /**
   * IndexedDB quota and usage: an evicted or full store explains a cache
   * that never fills, and Safari's eviction never reports itself.
   */
  private async logStorageEstimate(): Promise<void> {
    try {
      const estimate = await navigator.storage?.estimate?.();

      if (estimate) {
        this.info("storage estimate", {
          usage: estimate.usage,
          quota: estimate.quota,
        });
      }
    } catch {
      // Unsupported or blocked: nothing to record.
    }
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    void this.flush({ keepalive: true });
    this.removeListeners?.();
    this.removeListeners = null;
    this.started = false;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Sends the oldest queued entries as one batch. A transport failure or
   * server error re-queues them for the next flush; a 4xx (session gone,
   * payload refused) drops them, since retrying can't help.
   */
  async flush(options: { keepalive?: boolean } = {}): Promise<void> {
    if (!this.started || this.queue.length === 0) {
      return;
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    const batch = this.takeBatch(
      options.keepalive ? KEEPALIVE_MAX_BODY_BYTES : MAX_BODY_BYTES,
    );
    const fetchFn = this.options.fetch ?? globalThis.fetch;

    this.inFlight = (async () => {
      try {
        const response = await fetchFn(
          this.options.endpoint ?? TELEMETRY_ENDPOINT,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            keepalive: options.keepalive ?? false,
            body: JSON.stringify({
              sessionId: this.sessionId,
              sentAt: this.now(),
              entries: batch,
            }),
          },
        );

        if (!response.ok && response.status >= 500) {
          this.requeue(batch);
        }
      } catch {
        this.requeue(batch);
      } finally {
        this.inFlight = null;
      }
    })();

    await this.inFlight;

    if (this.queue.length > 0) {
      this.schedule();
    }
  }

  private now(): Date {
    return this.options.now ? this.options.now() : new Date();
  }

  private schedule(): void {
    if (!this.started || this.timer) {
      return;
    }

    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, TELEMETRY_FLUSH_INTERVAL_MS);
  }

  private takeBatch(maxBytes: number): TelemetryEntry[] {
    const batch: TelemetryEntry[] = [];
    let bytes = 0;

    while (this.queue.length > 0 && batch.length < CLIENT_LOG_MAX_BATCH_SIZE) {
      const entry = this.queue[0];
      const entryBytes = JSON.stringify(entry).length;

      if (batch.length > 0 && bytes + entryBytes > maxBytes) {
        break;
      }

      batch.push(this.queue.shift()!);
      bytes += entryBytes;
    }

    return batch;
  }

  private requeue(batch: TelemetryEntry[]): void {
    this.queue.unshift(...batch);
    this.trimQueue();
  }

  private trimQueue(): void {
    if (this.queue.length > TELEMETRY_MAX_QUEUE) {
      this.queue.splice(0, this.queue.length - TELEMETRY_MAX_QUEUE);
    }
  }

  private listen(): () => void {
    if (typeof window === "undefined") {
      return () => {};
    }

    const onPageHide = () => void this.flush({ keepalive: true });
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        void this.flush({ keepalive: true });
      }
    };
    const onOnline = () => this.info("browser online");
    const onOffline = () => this.warn("browser offline");
    const onError = (event: ErrorEvent) =>
      this.error("uncaught error", {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        error: event.error,
      });
    const onRejection = (event: PromiseRejectionEvent) =>
      this.error("unhandled rejection", { reason: event.reason });

    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }
}

function navigationType(): string | undefined {
  if (typeof performance === "undefined" || !performance.getEntriesByType) {
    return undefined;
  }

  const [entry] = performance.getEntriesByType("navigation");
  return entry && "type" in entry
    ? (entry as PerformanceNavigationTiming).type
    : undefined;
}

function currentPath(): string | undefined {
  if (typeof location === "undefined") {
    return undefined;
  }

  return `${location.pathname}${location.search}`;
}

function replacer(this: unknown, _key: string, value: unknown): unknown {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (value instanceof Map) {
    return Object.fromEntries(value);
  }

  if (value instanceof Set) {
    return [...value];
  }

  return value;
}

/**
 * Freezes `data` as plain JSON at log time — the objects callers hand us
 * (stores, ranges) keep mutating afterwards — and bounds its size so one
 * over-eager call can't ship a whole readings store.
 */
export function snapshot(data: unknown): unknown {
  if (data === undefined) {
    return undefined;
  }

  let json: string | undefined;
  try {
    json = JSON.stringify(data, replacer);
  } catch (err) {
    return { unserializable: String(err) };
  }

  if (json === undefined) {
    return undefined;
  }

  if (json.length > CLIENT_LOG_MAX_DATA_JSON_LENGTH) {
    return {
      truncated: true,
      length: json.length,
      preview: json.slice(0, DATA_PREVIEW_LENGTH),
    };
  }

  return JSON.parse(json);
}

export const telemetry = new Telemetry();
